import { scheduleToDays, toDisplayDayIndex } from '@/domain/templates/day-of-week';
import { fixedDayCount, type DayCountResolver } from '@/domain/templates/staffing';
import { calculateShiftDuration } from '@/domain/templates/template-utils';
import type { ShiftSet } from '@/types/template';

/**
 * Total number of individual shifts (one per head, per day) across a set of
 * patterns — the count of solid pills the live preview renders for the week.
 * For each pattern this sums its resolved head count over the days it runs.
 *
 * Uses head-count semantics (`resolveCount`, defaulting to the fixed staffing
 * count) so the total matches the pills the preview grid renders. This
 * **intentionally differs** from `template-utils.calculateTotalShifts`, which
 * uses per-day slot semantics for the wizard's review step; the two can disagree
 * for `open` shifts with a quantity > 1. Keep this one aligned with what
 * `LivePreview` draws — pass the same scaling resolver to match a scaled grid.
 */
export function countIndividualShifts(
  shifts: ShiftSet[],
  resolveCount: DayCountResolver = fixedDayCount,
): number {
  return shifts.reduce(
    (total, shift) =>
      total +
      scheduleToDays(shift.schedule).reduce(
        (sum, dayOfWeek) => sum + resolveCount(shift, dayOfWeek),
        0,
      ),
    0,
  );
}

/**
 * Headline preview totals for the week: the number of individual shifts, the
 * number of distinct days covered, and the total scheduled hours (shift
 * duration × heads, summed per day, overnight-aware). Drives the preview stats
 * strip. `resolveCount` defaults to the fixed staffing count; pass a scaling
 * resolver so the totals match a scaled grid.
 */
export function summarisePreview(
  shifts: ShiftSet[],
  resolveCount: DayCountResolver = fixedDayCount,
): {
  shifts: number;
  days: number;
  hours: number;
} {
  const coveredDays = new Set<number>();
  let hours = 0;

  for (const shift of shifts) {
    const duration = calculateShiftDuration(shift.startTime, shift.endTime);
    for (const dayOfWeek of scheduleToDays(shift.schedule)) {
      hours += duration * resolveCount(shift, dayOfWeek);
      coveredDays.add(toDisplayDayIndex(dayOfWeek));
    }
  }

  return { shifts: countIndividualShifts(shifts, resolveCount), days: coveredDays.size, hours };
}
